import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import bandService from "../../../../services/bandService";
import { eventService } from "../../../../services/eventService";
import friendshipService from "../../../../services/friendshipService";
import { uploadBandImage } from "../../../../services/mediaService";
import userService from "../../../../services/userService";
import useBandPosts from "../../../../hooks/useBandPosts";
import useBandShare from "../../../../hooks/useBandShare";
import useEventEditorActions from "../../../../hooks/useEventEditorActions";
import { enqueueBandFieldSave } from "../../../../utils/bandFieldSaveQueue";
import { appendQueryParam } from "../../../../utils/avatarCache";
import {
    buildBandPath,
    buildBandShareUrl,
    buildBandsPath,
    getBandRouteLocator,
    isBandsRoutePath,
} from "../../../../utils/bandRoutes";
import { buildProfileSharePath } from "../../../../utils/profileRoutes";
import { isActiveBandMembershipStatus, isPendingBandMembershipStatus } from "../../../../utils/bandMembership";
import { buildBandStyleTags, normalizeStyleTagList } from "../../../../utils/styleTags";
import {
    isUpcomingBandEvent,
    resolveBandAvatarImageUrl,
    resolveBandCoverImageUrl,
} from "../../BandPage/adaptBand";
import { BAND_UPCOMING_EVENT_TARGET_COUNT } from "../constants";
import useBandMediaLibrary from "./useBandMediaLibrary";
import {
    buildLocationDisplayUpdatePayload,
    buildRouteBandLookupKey,
    extractBandAvatarUrl,
    formatCreatedYear,
    getBandIdentity,
    mergeBandAvatarImage,
    mergeBandCoverImage,
    mergeUpdatedBandMember,
    normalizeFoundedYearDraft,
    removeBandMemberFromBand,
    selectedBandMatchesRouteLocator,
    toBandPageBand,
} from "../utils/bandWelcomeTransforms";
import {
    resolveUserAvatarUrl,
    resolveUserDisplayName,
    resolveUserUsername,
} from "../../../../utils/userIdentity";

const isRecord = (value) => Boolean(value && typeof value === "object" && !Array.isArray(value));
const BAND_INVITE_SEARCH_LIMIT = 30;
const BAND_FOLLOWER_PAGE_LIMIT = 24;
const BAND_FIELD_SAVE_FIELDS = ["name", "description", "styleTags", "foundedYear", "locationDisplay"];
const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value || {}, key);

const normalizeLocationNumber = (value) => (
    typeof value === "number" && Number.isFinite(value) ? value : null
);

const normalizeBandVisibility = (value) => {
    const normalized = typeof value === "string" ? value.trim().toUpperCase() : "";
    if (normalized !== "PUBLIC" && normalized !== "PRIVATE") {
        throw new Error("visibility must be PUBLIC or PRIVATE");
    }
    return normalized;
};

const readUserId = (entry) => {
    const value = entry?.userId ?? entry?.id ?? entry?.user?.id;
    return value == null ? "" : String(value).trim();
};

const buildInviteExcludedUserIds = (band, currentUserId) => {
    const excluded = new Set();
    if (currentUserId) {
        excluded.add(String(currentUserId));
    }
    (Array.isArray(band?.members) ? band.members : []).forEach((member) => {
        const userId = readUserId(member);
        if (userId) {
            excluded.add(userId);
        }
    });
    return excluded;
};

const normalizeInviteCandidates = (entries, excludedUserIds) => {
    const seen = new Set();
    return (Array.isArray(entries) ? entries : [])
        .map((entry) => {
            const userId = readUserId(entry);
            if (!userId || excludedUserIds.has(userId) || seen.has(userId)) {
                return null;
            }
            seen.add(userId);
            return {
                userId,
                username: resolveUserUsername(entry) || undefined,
                displayName: resolveUserDisplayName(entry) || resolveUserUsername(entry) || undefined,
                avatarUrl: resolveUserAvatarUrl(entry) || undefined,
            };
        })
        .filter(Boolean);
};

const mergeBandLocationPayload = (band, payload) => {
    if (!band) {
        return band;
    }
    const location = isRecord(payload?.location) ? payload.location : null;
    const rawLocationDisplay = payload?.locationDisplay ?? location?.formatted;
    const locationDisplay = typeof rawLocationDisplay === "string" && rawLocationDisplay.trim()
        ? rawLocationDisplay.trim()
        : null;

    if (!location) {
        return {
            ...band,
            location: null,
            locationDisplay: null,
            city: null,
            stateOrRegion: null,
            country: null,
            countryCode: null,
            latitude: null,
            longitude: null,
            coordinateLat: null,
            coordinateLng: null,
        };
    }

    const latitude = normalizeLocationNumber(location.latitude);
    const longitude = normalizeLocationNumber(location.longitude);
    return {
        ...band,
        location,
        locationDisplay,
        city: location.city ?? null,
        stateOrRegion: location.stateOrRegion ?? null,
        country: location.country ?? null,
        countryCode: location.countryCode ?? null,
        latitude,
        longitude,
        coordinateLat: latitude,
        coordinateLng: longitude,
    };
};

const readBandFieldValue = (band, field) => {
    if (!band || !field) {
        return undefined;
    }
    if (field === "styleTags") {
        return buildBandStyleTags(band);
    }
    return band[field];
};

const cloneBandFieldValue = (value) => (Array.isArray(value) ? [...value] : value);

const hasBandFieldValue = (band, field) => {
    if (!band || !field) {
        return false;
    }
    if (field === "styleTags") {
        return Array.isArray(band.styleTags) || Array.isArray(band.genres) || Array.isArray(band.styles);
    }
    return hasOwn(band, field);
};

const readBandFieldValues = (band, { requirePresence = false } = {}) => (
    BAND_FIELD_SAVE_FIELDS.reduce((values, field) => {
        if (!requirePresence || hasBandFieldValue(band, field)) {
            values[field] = cloneBandFieldValue(readBandFieldValue(band, field));
        }
        return values;
    }, {})
);

const applyBandFieldValue = (band, field, value) => {
    if (!band || !field) {
        return band;
    }
    if (field === "styleTags") {
        const nextTags = Array.isArray(value) ? value : normalizeStyleTagList(value, { splitCommas: true });
        return {
            ...band,
            styleTags: nextTags,
            genres: nextTags,
            styles: nextTags,
        };
    }
    return { ...band, [field]: value };
};

export default function useSelectedBandDetails({
    currentUser,
    location,
    navigate,
    setBands,
}) {
    const currentUserId = currentUser?.id || null;
    const routeBandLocator = useMemo(
        () => getBandRouteLocator(location.pathname),
        [location.pathname],
    );
    const routeBandLookupKey = useMemo(
        () => buildRouteBandLookupKey(routeBandLocator),
        [routeBandLocator],
    );

    const previewRequestIdRef = useRef(0);
    const bandFieldSaveQueueRef = useRef(null);
    const bandFieldSaveRevisionRef = useRef(0);
    const bandFieldSaveVersionByFieldRef = useRef({});
    const confirmedBandFieldValuesRef = useRef({ bandId: null, values: {} });
    const followRequestIdRef = useRef(0);
    const bandFollowersRequestIdRef = useRef(0);
    const inviteSearchRequestIdRef = useRef(0);
    const routeBandLookupKeyRef = useRef(null);
    const pendingBandPathRef = useRef(null);

    const [selectedBand, setSelectedBand] = useState(null);
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState(null);
    const [followStatus, setFollowStatus] = useState(null);
    const [isFollowLoading, setIsFollowLoading] = useState(false);
    const [bandFollowers, setBandFollowers] = useState([]);
    const [bandFollowersLoaded, setBandFollowersLoaded] = useState(false);
    const [isVisibilityUpdating, setIsVisibilityUpdating] = useState(false);
    const [isBandCoverUploadOpen, setIsBandCoverUploadOpen] = useState(false);
    const [isBandAvatarUploadOpen, setIsBandAvatarUploadOpen] = useState(false);
    const [bandCoverVersion, setBandCoverVersion] = useState(0);
    const [bandAvatarVersion, setBandAvatarVersion] = useState(0);
    const [isInviteMemberModalOpen, setIsInviteMemberModalOpen] = useState(false);
    const [inviteMemberSearchTerm, setInviteMemberSearchTerm] = useState("");
    const [inviteMemberCandidates, setInviteMemberCandidates] = useState([]);
    const [isInviteMemberLoading, setIsInviteMemberLoading] = useState(false);
    const [inviteMemberError, setInviteMemberError] = useState(null);
    const [invitingMemberUserId, setInvitingMemberUserId] = useState(null);
    const [memberPendingRemoval, setMemberPendingRemoval] = useState(null);
    const [isRemovingMember, setIsRemovingMember] = useState(false);
    const [memberRemoveError, setMemberRemoveError] = useState(null);
    const [isLeaveBandDialogOpen, setIsLeaveBandDialogOpen] = useState(false);
    const [isLeavingBand, setIsLeavingBand] = useState(false);
    const [leaveBandError, setLeaveBandError] = useState(null);

    const canEditSelectedBand = Boolean(
        currentUserId
            && selectedBand
            && !isPreviewLoading
            && selectedBand.currentUserIsOwner,
    );
    const bandContentAudience = currentUserId ? "viewer" : "public";

    const resetConfirmedBandFieldValues = useCallback(() => {
        confirmedBandFieldValuesRef.current = { bandId: null, values: {} };
    }, []);

    const mergeConfirmedBandFieldValues = useCallback((band, options = {}) => {
        const bandId = getBandIdentity(band);
        if (!bandId) {
            if (options.replace) {
                resetConfirmedBandFieldValues();
            }
            return;
        }

        const previous = confirmedBandFieldValuesRef.current;
        const values = options.replace || previous.bandId !== bandId
            ? {}
            : { ...(previous.values || {}) };

        confirmedBandFieldValuesRef.current = {
            bandId,
            values: {
                ...values,
                ...readBandFieldValues(band, { requirePresence: options.requirePresence === true }),
            },
        };
    }, [resetConfirmedBandFieldValues]);

    const readConfirmedBandFieldValue = useCallback((bandId, field, fallbackBand) => {
        const confirmed = confirmedBandFieldValuesRef.current;
        if (confirmed.bandId !== bandId || !hasOwn(confirmed.values, field)) {
            mergeConfirmedBandFieldValues(fallbackBand, {
                replace: confirmed.bandId !== bandId,
            });
        }

        const nextConfirmed = confirmedBandFieldValuesRef.current;
        if (nextConfirmed.bandId === bandId && hasOwn(nextConfirmed.values, field)) {
            return cloneBandFieldValue(nextConfirmed.values[field]);
        }
        return cloneBandFieldValue(readBandFieldValue(fallbackBand, field));
    }, [mergeConfirmedBandFieldValues]);

    const markConfirmedBandFieldValue = useCallback((bandId, field, value) => {
        if (!bandId || !field) {
            return;
        }
        const confirmed = confirmedBandFieldValuesRef.current;
        confirmedBandFieldValuesRef.current = {
            bandId,
            values: {
                ...(confirmed.bandId === bandId ? confirmed.values || {} : {}),
                [field]: cloneBandFieldValue(value),
            },
        };
    }, []);

    const mergeFollowStatusIntoBand = useCallback((band, status) => {
        if (!band || !status || status.bandId !== band.id) {
            return band;
        }
        const nextBand = {
            ...band,
            currentUserIsFollowing: status.following === true,
        };
        if (Number.isFinite(status.followerCount)) {
            nextBand.followerCount = status.followerCount;
        }
        return nextBand;
    }, []);

    const applyFollowStatus = useCallback((status) => {
        if (!status?.bandId) {
            return;
        }
        setSelectedBand((current) => mergeFollowStatusIntoBand(current, status));
        setBands((current) => current.map((band) => mergeFollowStatusIntoBand(band, status)));
    }, [mergeFollowStatusIntoBand, setBands]);

    const loadBandEvents = useCallback(async (bandId, { audience = "viewer" } = {}) => {
        if (!bandId) return [];
        const loadEvents = audience === "public"
            ? eventService.getPublicBandEvents
            : eventService.getBandEvents;
        const events = await loadEvents(bandId, {
            limit: BAND_UPCOMING_EVENT_TARGET_COUNT,
            offset: 0,
            upcomingOnly: true,
        });
        return Array.isArray(events)
            ? events.filter(isUpcomingBandEvent).slice(0, BAND_UPCOMING_EVENT_TARGET_COUNT)
            : [];
    }, []);

    const refreshSelectedBandEvents = useCallback(async () => {
        if (!selectedBand?.id) return;
        try {
            const events = await loadBandEvents(selectedBand.id, {
                audience: bandContentAudience,
            });
            setSelectedBand((prev) => (prev?.id === selectedBand.id ? { ...prev, events } : prev));
        } catch (_err) {
            // Keep the current schedule visible; explicit create/update/delete
            // failures are surfaced by the modal or confirmation dialog.
        }
    }, [bandContentAudience, loadBandEvents, selectedBand?.id]);

    const mergeSelectedBandEvent = useCallback((event) => {
        if (!event?.id) return;
        setSelectedBand((prev) => {
            if (!prev) return prev;
            const existing = Array.isArray(prev.events) ? prev.events : [];
            const nextEvents = existing.some((item) => item?.id === event.id)
                ? existing.map((item) => (item?.id === event.id ? event : item))
                : [event, ...existing];
            return {
                ...prev,
                events: nextEvents,
            };
        });
    }, []);

    const removeSelectedBandEvent = useCallback((event) => {
        if (!event?.id) return;
        setSelectedBand((prev) => {
            if (!prev) return prev;
            const existing = Array.isArray(prev.events) ? prev.events : [];
            return {
                ...prev,
                events: existing.filter((item) => item?.id !== event.id),
            };
        });
    }, []);

    const {
        isEventModalOpen,
        eventModalMode,
        eventBeingEdited,
        eventToDelete,
        isDeletingEvent: isDeletingBandEvent,
        eventDeleteError: bandEventDeleteError,
        resetEventUiState: resetBandEventUiState,
        openCreateEvent: handleOpenCreateEvent,
        openEditEvent: handleOpenEditEvent,
        closeEventModal: handleCloseEventModal,
        handleEventCreated: handleBandEventCreated,
        handleEventUpdated: handleBandEventUpdated,
        handleEventDeleted: handleBandEventDeleted,
        requestDeleteEvent: handleRequestDeleteBandEvent,
        cancelDeleteEvent: handleCancelDeleteBandEvent,
        confirmDeleteEvent: handleConfirmDeleteBandEvent,
    } = useEventEditorActions({
        canManage: canEditSelectedBand,
        onEventCreated: mergeSelectedBandEvent,
        onEventUpdated: mergeSelectedBandEvent,
        onEventDeleted: removeSelectedBandEvent,
        refreshEvents: refreshSelectedBandEvents,
        getMutationScope: () => previewRequestIdRef.current,
        logLabel: "band event",
    });

    const bandMedia = useBandMediaLibrary({
        selectedBand,
        currentUserId,
        canEditSelectedBand,
        audience: bandContentAudience,
        previewRequestIdRef,
    });
    const resetBandMediaState = bandMedia.resetBandMediaState;

    const {
        shareBand,
        bandShareToast,
        clearBandShareToast,
    } = useBandShare(selectedBand);

    const beginPreviewLoad = useCallback(() => {
        const requestId = previewRequestIdRef.current + 1;
        previewRequestIdRef.current = requestId;
        resetBandMediaState();
        resetConfirmedBandFieldValues();
        setPreviewError(null);
        setIsPreviewLoading(true);
        setFollowStatus(null);
        setIsFollowLoading(false);
        bandFollowersRequestIdRef.current += 1;
        setBandFollowers([]);
        setBandFollowersLoaded(false);
        setIsVisibilityUpdating(false);
        setIsInviteMemberModalOpen(false);
        setInviteMemberSearchTerm("");
        setInviteMemberCandidates([]);
        setInviteMemberError(null);
        setInvitingMemberUserId(null);
        setMemberPendingRemoval(null);
        setIsRemovingMember(false);
        setMemberRemoveError(null);
        setIsLeaveBandDialogOpen(false);
        setIsLeavingBand(false);
        setLeaveBandError(null);
        resetBandEventUiState();
        return requestId;
    }, [resetBandEventUiState, resetBandMediaState, resetConfirmedBandFieldValues]);

    const clearSelectedBandPreview = useCallback(({ clearRouteRefs = false } = {}) => {
        previewRequestIdRef.current += 1;
        resetBandMediaState();
        resetConfirmedBandFieldValues();
        setSelectedBand(null);
        setPreviewError(null);
        setIsPreviewLoading(false);
        setFollowStatus(null);
        setIsFollowLoading(false);
        bandFollowersRequestIdRef.current += 1;
        setBandFollowers([]);
        setBandFollowersLoaded(false);
        setIsVisibilityUpdating(false);
        setIsInviteMemberModalOpen(false);
        setInviteMemberSearchTerm("");
        setInviteMemberCandidates([]);
        setInviteMemberError(null);
        setInvitingMemberUserId(null);
        setMemberPendingRemoval(null);
        setIsRemovingMember(false);
        setMemberRemoveError(null);
        setIsLeaveBandDialogOpen(false);
        setIsLeavingBand(false);
        setLeaveBandError(null);
        resetBandEventUiState();
        if (clearRouteRefs) {
            routeBandLookupKeyRef.current = null;
            pendingBandPathRef.current = null;
        }
    }, [resetBandEventUiState, resetBandMediaState, resetConfirmedBandFieldValues]);

    useEffect(() => {
        setIsBandCoverUploadOpen(false);
        setIsBandAvatarUploadOpen(false);
        setBandCoverVersion(0);
        setBandAvatarVersion(0);
    }, [selectedBand?.id]);

    const loadInviteMemberCandidates = useCallback(async (query = "") => {
        if (!canEditSelectedBand) {
            setInviteMemberCandidates([]);
            return;
        }
        const requestId = inviteSearchRequestIdRef.current + 1;
        inviteSearchRequestIdRef.current = requestId;
        const normalizedQuery = typeof query === "string" ? query.trim() : "";
        setIsInviteMemberLoading(true);
        setInviteMemberError(null);
        try {
            const entries = normalizedQuery
                ? (await userService.searchUsersPage({
                    query: normalizedQuery,
                    limit: BAND_INVITE_SEARCH_LIMIT,
                }))?.items
                : await friendshipService.getAcceptedFriends();
            if (inviteSearchRequestIdRef.current !== requestId) {
                return;
            }
            setInviteMemberCandidates(normalizeInviteCandidates(
                entries,
                buildInviteExcludedUserIds(selectedBand, currentUserId),
            ));
        } catch (error) {
            if (inviteSearchRequestIdRef.current !== requestId) {
                return;
            }
            setInviteMemberError(error);
            setInviteMemberCandidates([]);
        } finally {
            if (inviteSearchRequestIdRef.current === requestId) {
                setIsInviteMemberLoading(false);
            }
        }
    }, [canEditSelectedBand, currentUserId, selectedBand]);

    useEffect(() => {
        if (!isInviteMemberModalOpen) {
            return undefined;
        }
        const delay = inviteMemberSearchTerm.trim() ? 220 : 0;
        const handle = window.setTimeout(
            () => loadInviteMemberCandidates(inviteMemberSearchTerm),
            delay,
        );
        return () => window.clearTimeout(handle);
    }, [inviteMemberSearchTerm, isInviteMemberModalOpen, loadInviteMemberCandidates]);

    const handleOpenInviteMemberModal = useCallback(() => {
        if (!canEditSelectedBand) {
            return;
        }
        setInviteMemberSearchTerm("");
        setInviteMemberCandidates([]);
        setInviteMemberError(null);
        setInvitingMemberUserId(null);
        setIsInviteMemberModalOpen(true);
    }, [canEditSelectedBand]);

    const handleCloseInviteMemberModal = useCallback(() => {
        if (invitingMemberUserId) {
            return;
        }
        inviteSearchRequestIdRef.current += 1;
        setIsInviteMemberModalOpen(false);
        setInviteMemberSearchTerm("");
        setInviteMemberCandidates([]);
        setInviteMemberError(null);
    }, [invitingMemberUserId]);

    const handleOpenBand = useCallback(async (band) => {
        if (!band?.id) return;
        const nextBandPath = buildBandPath(band);
        const nextRouteBandLookupKey = buildRouteBandLookupKey(getBandRouteLocator(nextBandPath));
        if (nextRouteBandLookupKey) {
            routeBandLookupKeyRef.current = nextRouteBandLookupKey;
        }
        if (nextBandPath && location.pathname !== nextBandPath) {
            pendingBandPathRef.current = nextBandPath;
            navigate(nextBandPath);
        }
        const requestId = beginPreviewLoad();
        const previewBand = toBandPageBand(null, band);
        mergeConfirmedBandFieldValues(previewBand, { replace: true });
        setSelectedBand(previewBand);
        try {
            const details = await bandService.getBandDetails(band.id);
            const events = await loadBandEvents(band.id, {
                audience: bandContentAudience,
            }).catch(() => []);
            if (previewRequestIdRef.current !== requestId) {
                return;
            }
            const nextBand = toBandPageBand({ ...details, events }, band);
            mergeConfirmedBandFieldValues(nextBand, { replace: true });
            setSelectedBand(nextBand);
        } catch (err) {
            if (previewRequestIdRef.current !== requestId) {
                return;
            }
            setPreviewError(err);
        } finally {
            if (previewRequestIdRef.current === requestId) {
                setIsPreviewLoading(false);
            }
        }
    }, [
        bandContentAudience,
        beginPreviewLoad,
        loadBandEvents,
        location.pathname,
        mergeConfirmedBandFieldValues,
        navigate,
    ]);

    const handleClosePreview = useCallback(() => {
        clearSelectedBandPreview({ clearRouteRefs: true });
        if (routeBandLookupKey) {
            navigate(buildBandsPath());
        }
    }, [clearSelectedBandPreview, navigate, routeBandLookupKey]);

    const openBandByRouteLocator = useCallback(async (locator) => {
        const lookupKey = buildRouteBandLookupKey(locator);
        if (!lookupKey) {
            return;
        }
        const requestId = beginPreviewLoad();
        setSelectedBand(null);
        try {
            const details = await bandService.getBandByHandle(locator.value);
            const resolvedBandId = details?.id || details?.objectId || null;
            const events = resolvedBandId
                ? await loadBandEvents(resolvedBandId, {
                    audience: bandContentAudience,
                }).catch(() => [])
                : [];
            if (previewRequestIdRef.current !== requestId) {
                return;
            }
            const nextBand = toBandPageBand(
                { ...details, events },
                { id: resolvedBandId || undefined, handle: locator.value || "", name: locator.value || "" },
            );
            mergeConfirmedBandFieldValues(nextBand, { replace: true });
            setSelectedBand(nextBand);
        } catch (err) {
            if (previewRequestIdRef.current !== requestId) {
                return;
            }
            setPreviewError(err);
        } finally {
            if (previewRequestIdRef.current === requestId) {
                setIsPreviewLoading(false);
            }
        }
    }, [bandContentAudience, beginPreviewLoad, loadBandEvents, mergeConfirmedBandFieldValues]);

    useEffect(() => {
        if (pendingBandPathRef.current === location.pathname) {
            pendingBandPathRef.current = null;
        }

        if (!routeBandLookupKey) {
            if (pendingBandPathRef.current) {
                return;
            }
            const bandsPath = buildBandsPath();
            const shouldReplaceInvalidNestedBandPath = location.pathname !== bandsPath
                && isBandsRoutePath(location.pathname);
            const hasRoutePreviewState = routeBandLookupKeyRef.current !== null
                || Boolean(selectedBand)
                || isPreviewLoading
                || Boolean(previewError);
            routeBandLookupKeyRef.current = null;
            if (hasRoutePreviewState) {
                clearSelectedBandPreview();
            }
            if (shouldReplaceInvalidNestedBandPath) {
                navigate(bandsPath, { replace: true });
            }
            return;
        }

        if (routeBandLookupKeyRef.current === routeBandLookupKey) {
            pendingBandPathRef.current = null;
            return;
        }

        if (selectedBandMatchesRouteLocator(selectedBand, routeBandLocator)) {
            routeBandLookupKeyRef.current = routeBandLookupKey;
            pendingBandPathRef.current = null;
            return;
        }

        routeBandLookupKeyRef.current = routeBandLookupKey;
        pendingBandPathRef.current = null;
        openBandByRouteLocator(routeBandLocator);
    }, [
        clearSelectedBandPreview,
        isPreviewLoading,
        location.pathname,
        navigate,
        openBandByRouteLocator,
        previewError,
        routeBandLocator,
        routeBandLookupKey,
        selectedBand,
    ]);

    useEffect(() => {
        const bandId = selectedBand?.id;
        if (!bandId || !currentUserId || selectedBand.currentUserIsOwner) {
            setFollowStatus(null);
            setIsFollowLoading(false);
            return undefined;
        }

        const requestId = followRequestIdRef.current + 1;
        followRequestIdRef.current = requestId;
        setIsFollowLoading(true);
        bandService.getBandFollowStatus(bandId)
            .then((status) => {
                if (followRequestIdRef.current !== requestId) return;
                const nextStatus = status && typeof status === "object" ? status : null;
                setFollowStatus(nextStatus);
                applyFollowStatus(nextStatus);
            })
            .catch(() => {
                if (followRequestIdRef.current !== requestId) return;
                setFollowStatus(null);
            })
            .finally(() => {
                if (followRequestIdRef.current === requestId) {
                    setIsFollowLoading(false);
                }
            });

        return () => {
            followRequestIdRef.current += 1;
        };
    }, [applyFollowStatus, currentUserId, selectedBand?.currentUserIsOwner, selectedBand?.id]);

    useEffect(() => {
        const bandId = selectedBand?.id;
        const previewRequestId = previewRequestIdRef.current;
        const requestId = bandFollowersRequestIdRef.current + 1;
        bandFollowersRequestIdRef.current = requestId;
        if (!bandId || !currentUserId) {
            setBandFollowers([]);
            setBandFollowersLoaded(false);
            return undefined;
        }

        setBandFollowers([]);
        setBandFollowersLoaded(false);
        bandService.getBandFollowersPage(bandId, { limit: BAND_FOLLOWER_PAGE_LIMIT })
            .then((page) => {
                if (
                    bandFollowersRequestIdRef.current !== requestId
                    || previewRequestIdRef.current !== previewRequestId
                ) {
                    return;
                }
                setBandFollowers(Array.isArray(page?.items) ? page.items : []);
                setBandFollowersLoaded(true);
            })
            .catch((error) => {
                if (
                    bandFollowersRequestIdRef.current !== requestId
                    || previewRequestIdRef.current !== previewRequestId
                ) {
                    return;
                }
                console.warn("Failed to load band followers.", error);
                setBandFollowers([]);
                setBandFollowersLoaded(true);
            });

        return () => {
            if (bandFollowersRequestIdRef.current === requestId) {
                bandFollowersRequestIdRef.current += 1;
            }
        };
    }, [currentUserId, selectedBand?.followerCount, selectedBand?.id]);

    const selectedBandIsFollowing = Boolean(
        selectedBand
            && (followStatus?.bandId === selectedBand.id
                ? followStatus.following === true
                : selectedBand.currentUserIsFollowing === true),
    );

    const handleToggleSelectedBandFollow = useCallback(async () => {
        if (!selectedBand?.id) {
            return;
        }
        const requestId = followRequestIdRef.current + 1;
        followRequestIdRef.current = requestId;
        const bandId = selectedBand.id;
        setIsFollowLoading(true);
        setPreviewError(null);
        try {
            const shouldUnfollow = followStatus?.bandId === bandId
                ? followStatus.following === true
                : selectedBand.currentUserIsFollowing === true;
            const status = shouldUnfollow
                ? await bandService.unfollowBand(bandId)
                : await bandService.followBand(bandId);
            if (followRequestIdRef.current !== requestId) {
                return;
            }
            const nextStatus = status && typeof status === "object" ? status : null;
            setFollowStatus(nextStatus);
            applyFollowStatus(nextStatus);
        } catch (err) {
            if (followRequestIdRef.current !== requestId) {
                return;
            }
            setPreviewError(err);
        } finally {
            if (followRequestIdRef.current === requestId) {
                setIsFollowLoading(false);
            }
        }
    }, [applyFollowStatus, followStatus, selectedBand]);

    const canShowFollowSelectedBand = Boolean(
        currentUserId
            && selectedBand
            && !previewError
            && !isPreviewLoading
            && !selectedBand.currentUserIsOwner,
    );

    const selectedBandCoverRawUrl = useMemo(
        () => resolveBandCoverImageUrl(selectedBand) || null,
        [selectedBand],
    );

    const selectedBandCoverUrl = useMemo(() => {
        if (!selectedBandCoverRawUrl) {
            return null;
        }
        return bandCoverVersion > 0
            ? appendQueryParam(selectedBandCoverRawUrl, "v", bandCoverVersion)
            : selectedBandCoverRawUrl;
    }, [bandCoverVersion, selectedBandCoverRawUrl]);

    const selectedBandAvatarRawUrl = useMemo(
        () => (
            resolveBandAvatarImageUrl(selectedBand)
            || extractBandAvatarUrl(selectedBand)
            || selectedBand?.avatarUrl
            || selectedBand?.iconUrl
            || null
        ),
        [selectedBand],
    );

    const selectedBandAvatarUrl = useMemo(() => {
        if (!selectedBandAvatarRawUrl) {
            return null;
        }
        return bandAvatarVersion > 0
            ? appendQueryParam(selectedBandAvatarRawUrl, "v", bandAvatarVersion)
            : selectedBandAvatarRawUrl;
    }, [bandAvatarVersion, selectedBandAvatarRawUrl]);

    const bandCoverUploadConfig = useMemo(() => ({
        maxSizeMb: 5,
        getHistory: async () => [],
        upload: async (_formData, { selectedFile } = {}) => {
            if (!selectedFile) {
                throw new Error("Band cover upload requires an image file.");
            }
            if (!canEditSelectedBand || !selectedBand?.id) {
                throw new Error("Band cover updates require owner access.");
            }
            const uploadResponse = await uploadBandImage(selectedBand.id, selectedFile);
            const nextUrl = uploadResponse?.url;
            if (!nextUrl) {
                throw new Error("Band cover upload did not return an image URL.");
            }
            const nextBand = mergeBandCoverImage(selectedBand, nextUrl, uploadResponse);
            const updatedBand = await bandService.updateBand(selectedBand.id, {
                images: nextBand.images,
            });
            return {
                ...uploadResponse,
                url: nextUrl,
                band: updatedBand,
            };
        },
    }), [canEditSelectedBand, selectedBand]);

    const bandAvatarUploadConfig = useMemo(() => ({
        i18nKey: "bandAvatar",
        maxSizeMb: 5,
        getHistory: async () => [],
        upload: async (_formData, { selectedFile } = {}) => {
            if (!selectedFile) {
                throw new Error("Band avatar upload requires an image file.");
            }
            if (!canEditSelectedBand || !selectedBand?.id) {
                throw new Error("Band avatar updates require owner access.");
            }
            const uploadResponse = await uploadBandImage(selectedBand.id, selectedFile);
            const nextUrl = uploadResponse?.url;
            if (!nextUrl) {
                throw new Error("Band avatar upload did not return an image URL.");
            }
            let refreshedBand = null;
            try {
                refreshedBand = await bandService.getBandDetails(selectedBand.id);
            } catch (_err) {
                refreshedBand = null;
            }
            return {
                ...uploadResponse,
                url: nextUrl,
                band: refreshedBand,
            };
        },
    }), [canEditSelectedBand, selectedBand]);

    const handleOpenBandCoverUpload = useCallback(() => {
        if (!canEditSelectedBand) {
            return;
        }
        setIsBandCoverUploadOpen(true);
    }, [canEditSelectedBand]);

    const handleCloseBandCoverUpload = useCallback(() => {
        setIsBandCoverUploadOpen(false);
    }, []);

    const handleOpenBandAvatarUpload = useCallback(() => {
        if (!canEditSelectedBand) {
            return;
        }
        setIsBandAvatarUploadOpen(true);
    }, [canEditSelectedBand]);

    const handleCloseBandAvatarUpload = useCallback(() => {
        setIsBandAvatarUploadOpen(false);
    }, []);

    const handleBandCoverUploaded = useCallback((nextUrl, response) => {
        if (!nextUrl || !selectedBand?.id) {
            return;
        }
        const bandId = selectedBand.id;
        const uploadedBand = response?.band && typeof response.band === "object"
            ? mergeBandCoverImage(response.band, nextUrl, response)
            : null;
        setSelectedBand((current) => (
            current?.id === bandId
                ? (uploadedBand ? toBandPageBand(uploadedBand, current) : mergeBandCoverImage(current, nextUrl))
                : current
        ));
        setBands((current) => current.map((band) => (
            band?.id === bandId
                ? (uploadedBand ? { ...band, ...uploadedBand } : mergeBandCoverImage(band, nextUrl))
                : band
        )));
        setBandCoverVersion((current) => current + 1);
    }, [selectedBand?.id, setBands]);

    const handleBandAvatarUploaded = useCallback((nextUrl, response) => {
        if (!nextUrl || !selectedBand?.id) {
            return;
        }
        const bandId = selectedBand.id;
        const uploadedBand = response?.band && typeof response.band === "object"
            ? mergeBandAvatarImage(response.band, nextUrl, response)
            : null;
        setSelectedBand((current) => (
            current?.id === bandId
                ? (uploadedBand ? toBandPageBand(uploadedBand, current) : mergeBandAvatarImage(current, nextUrl))
                : current
        ));
        setBands((current) => current.map((band) => (
            band?.id === bandId
                ? (uploadedBand ? { ...band, ...uploadedBand } : mergeBandAvatarImage(band, nextUrl))
                : band
        )));
        setBandAvatarVersion((current) => current + 1);
    }, [selectedBand?.id, setBands]);

    const bandEventOrganizer = useMemo(() => (
        canEditSelectedBand && selectedBand?.id
            ? { objectType: "BAND", objectId: selectedBand.id }
            : null
    ), [canEditSelectedBand, selectedBand?.id]);

    const bandPosts = useBandPosts(selectedBand, currentUser, {
        enabled: Boolean(selectedBand?.id),
        audience: bandContentAudience,
    });

    const bandPostComposerProps = useMemo(() => ({
        isComposerOpen: bandPosts.isComposerOpen,
        composerMode: bandPosts.composerMode,
        openCreateComposer: bandPosts.openCreateComposer,
        startEditingPost: bandPosts.startEditingPost,
        closeComposer: bandPosts.closeComposer,
        editingPost: bandPosts.editingPost,
        postContent: bandPosts.postContent,
        setPostContent: bandPosts.setPostContent,
        postVisibility: bandPosts.postVisibility,
        setPostVisibility: bandPosts.setPostVisibility,
        editContent: bandPosts.editContent,
        setEditContent: bandPosts.setEditContent,
        editVisibility: bandPosts.editVisibility,
        setEditVisibility: bandPosts.setEditVisibility,
        postAttachments: bandPosts.postAttachments,
        editAttachments: bandPosts.editAttachments,
        addCreateAttachments: bandPosts.addCreateAttachments,
        removeCreateAttachment: bandPosts.removeCreateAttachment,
        addEditAttachments: bandPosts.addEditAttachments,
        removeEditAttachment: bandPosts.removeEditAttachment,
        postAttachmentError: bandPosts.postAttachmentError,
        editAttachmentError: bandPosts.editAttachmentError,
        isSubmitting: bandPosts.isSubmitting,
        maxAttachments: bandPosts.maxAttachments,
        createPost: bandPosts.createPost,
        handleUpdatePost: bandPosts.updatePost,
        deletePost: bandPosts.deletePost,
        onPostMetaChange: bandPosts.updatePostMeta,
        hasMorePosts: bandPosts.hasMorePosts,
        isLoadingPosts: bandPosts.isLoadingPosts,
        isLoadingMorePosts: bandPosts.isLoadingMorePosts,
        postsError: bandPosts.postsError,
        onRetryPosts: bandPosts.retryFetchPosts,
        loadMorePosts: bandPosts.loadMorePosts,
    }), [bandPosts]);

    const refreshSelectedBandDetails = useCallback(async ({ signal } = {}) => {
        if (!selectedBand?.id) return;
        const requestId = previewRequestIdRef.current;
        try {
            const details = await bandService.getBandDetails(selectedBand.id, { signal });
            if (previewRequestIdRef.current !== requestId) return;
            mergeConfirmedBandFieldValues(details, { requirePresence: true });
            setSelectedBand((prev) => (prev ? toBandPageBand(details, prev) : prev));
            setBands((current) => current.map((band) => (
                band?.id === selectedBand.id ? { ...band, ...details } : band
            )));
        } catch (_err) {
            /* the save itself succeeded; keep the optimistic state if refresh fails. */
        }
    }, [mergeConfirmedBandFieldValues, selectedBand, setBands]);

    const handleSelectedBandVisibilityChange = useCallback(async (nextValue) => {
        if (!canEditSelectedBand || !selectedBand?.id || isVisibilityUpdating) {
            return;
        }
        const bandId = selectedBand.id;
        const nextVisibility = normalizeBandVisibility(nextValue);
        const previousBand = selectedBand;
        if (normalizeBandVisibility(previousBand.visibility || "PUBLIC") === nextVisibility) {
            return;
        }
        const requestId = previewRequestIdRef.current;
        setPreviewError(null);
        setIsVisibilityUpdating(true);
        setSelectedBand((current) => (
            current?.id === bandId ? { ...current, visibility: nextVisibility } : current
        ));
        setBands((current) => current.map((band) => (
            band?.id === bandId ? { ...band, visibility: nextVisibility } : band
        )));
        try {
            const updatedBand = await bandService.updateBandVisibility(bandId, nextVisibility);
            if (previewRequestIdRef.current !== requestId) {
                return;
            }
            setSelectedBand((current) => (
                current?.id === bandId ? toBandPageBand(updatedBand, current) : current
            ));
            setBands((current) => current.map((band) => (
                band?.id === bandId ? { ...band, ...updatedBand, visibility: nextVisibility } : band
            )));
            await refreshSelectedBandDetails();
        } catch (err) {
            if (previewRequestIdRef.current === requestId) {
                setSelectedBand((current) => (current?.id === bandId ? previousBand : current));
                setBands((current) => current.map((band) => (
                    band?.id === bandId ? { ...band, visibility: previousBand.visibility } : band
                )));
                setPreviewError(err);
            }
        } finally {
            if (previewRequestIdRef.current === requestId) {
                setIsVisibilityUpdating(false);
            }
        }
    }, [
        canEditSelectedBand,
        isVisibilityUpdating,
        refreshSelectedBandDetails,
        selectedBand,
        setBands,
    ]);

    const handleOpenSelectedBandPublicView = useCallback(() => {
        const url = buildBandShareUrl(selectedBand);
        if (!url || typeof window === "undefined") {
            return;
        }
        window.open(url, "_blank", "noopener,noreferrer");
    }, [selectedBand]);

    const handleOpenUserProfile = useCallback((entry, selectedUserId) => {
        const profilePath = buildProfileSharePath({
            userId: selectedUserId
                ?? entry?.userId
                ?? entry?.id
                ?? entry?.user?.id
                ?? entry?.profile?.userId
                ?? entry?.profile?.id,
            username: entry?.username
                ?? entry?.user?.username
                ?? entry?.profile?.username,
        });
        if (profilePath) {
            navigate(profilePath);
        }
    }, [navigate]);

    const handleInviteBandMember = useCallback(async (candidate) => {
        const bandId = selectedBand?.id;
        const userId = candidate?.userId;
        if (!canEditSelectedBand || !bandId || !userId || invitingMemberUserId) {
            return;
        }
        const requestId = previewRequestIdRef.current;
        setInvitingMemberUserId(userId);
        setInviteMemberError(null);
        try {
            await bandService.inviteMember(bandId, userId);
            if (previewRequestIdRef.current !== requestId) {
                return;
            }
            setInviteMemberCandidates((current) => current.filter((entry) => entry.userId !== userId));
            await refreshSelectedBandDetails();
        } catch (err) {
            if (previewRequestIdRef.current === requestId) {
                setInviteMemberError(err);
            }
        } finally {
            if (previewRequestIdRef.current === requestId) {
                setInvitingMemberUserId(null);
            }
        }
    }, [canEditSelectedBand, invitingMemberUserId, refreshSelectedBandDetails, selectedBand?.id]);

    const handleRequestRemoveBandMember = useCallback((member) => {
        const userId = member?.userId == null ? "" : String(member.userId).trim();
        const membershipRole = String(member?.membershipRole || member?.role || "").trim().toUpperCase();
        if (
            !canEditSelectedBand
            || !userId
            || (currentUserId && userId === String(currentUserId))
            || membershipRole === "OWNER"
            || isRemovingMember
        ) {
            return;
        }
        setMemberRemoveError(null);
        setMemberPendingRemoval({
            ...member,
            userId,
            displayName: resolveUserDisplayName(member) || resolveUserUsername(member) || userId,
        });
    }, [canEditSelectedBand, currentUserId, isRemovingMember]);

    const handleCancelRemoveBandMember = useCallback(() => {
        if (isRemovingMember) {
            return;
        }
        setMemberPendingRemoval(null);
        setMemberRemoveError(null);
    }, [isRemovingMember]);

    const handleConfirmRemoveBandMember = useCallback(async () => {
        const bandId = selectedBand?.id;
        const userId = memberPendingRemoval?.userId;
        const removedMemberIsPending = Boolean(memberPendingRemoval?.isPending);
        if (!canEditSelectedBand || !bandId || !userId || isRemovingMember) {
            return;
        }
        const requestId = previewRequestIdRef.current;
        setIsRemovingMember(true);
        setMemberRemoveError(null);
        try {
            await bandService.removeMember(bandId, userId);
            if (previewRequestIdRef.current !== requestId) {
                return;
            }
            setSelectedBand((current) => (
                current?.id === bandId ? removeBandMemberFromBand(current, userId, { removedMemberIsPending }) : current
            ));
            setBands((current) => current.map((band) => (
                band?.id === bandId ? removeBandMemberFromBand(band, userId, { removedMemberIsPending }) : band
            )));
            setMemberPendingRemoval(null);
            await refreshSelectedBandDetails();
        } catch (err) {
            if (previewRequestIdRef.current === requestId) {
                setMemberRemoveError(err);
            }
        } finally {
            if (previewRequestIdRef.current === requestId) {
                setIsRemovingMember(false);
            }
        }
    }, [
        canEditSelectedBand,
        isRemovingMember,
        memberPendingRemoval?.isPending,
        memberPendingRemoval?.userId,
        refreshSelectedBandDetails,
        selectedBand?.id,
        setBands,
    ]);

    const canLeaveSelectedBand = Boolean(
        currentUserId
            && selectedBand?.id
            && selectedBand.currentUserIsMember === true
            && isActiveBandMembershipStatus(selectedBand.currentUserMembershipStatus),
    );

    const handleRequestLeaveBand = useCallback(() => {
        if (!canLeaveSelectedBand || isLeavingBand) {
            return;
        }
        setLeaveBandError(null);
        setIsLeaveBandDialogOpen(true);
    }, [canLeaveSelectedBand, isLeavingBand]);

    const handleCancelLeaveBand = useCallback(() => {
        if (isLeavingBand) {
            return;
        }
        setIsLeaveBandDialogOpen(false);
        setLeaveBandError(null);
    }, [isLeavingBand]);

    const handleConfirmLeaveBand = useCallback(async () => {
        const bandId = selectedBand?.id;
        if (!canLeaveSelectedBand || !bandId || !currentUserId || isLeavingBand) {
            return;
        }
        const requestId = previewRequestIdRef.current;
        setIsLeavingBand(true);
        setLeaveBandError(null);
        try {
            await bandService.leaveBand(bandId);
            if (previewRequestIdRef.current !== requestId) {
                return;
            }
            const applyLeave = (band) => {
                const updated = removeBandMemberFromBand(band, currentUserId);
                return updated
                    ? {
                        ...updated,
                        currentUserIsOwner: false,
                        currentUserIsMember: false,
                        currentUserMembershipStatus: null,
                    }
                    : updated;
            };
            const clearPublicMembership = (current) => (Array.isArray(current) ? current : []).map((band) => (
                getBandIdentity(band) === bandId ? applyLeave(band) : band
            ));
            const removeFromMyBands = (current) => (Array.isArray(current) ? current : [])
                .filter((band) => getBandIdentity(band) !== bandId);
            setSelectedBand((current) => (getBandIdentity(current) === bandId ? applyLeave(current) : current));
            setBands(clearPublicMembership, removeFromMyBands);
            setIsLeaveBandDialogOpen(false);
            await refreshSelectedBandDetails().catch(() => {});
        } catch (err) {
            if (previewRequestIdRef.current === requestId) {
                setLeaveBandError(err);
            }
        } finally {
            if (previewRequestIdRef.current === requestId) {
                setIsLeavingBand(false);
            }
        }
    }, [
        canLeaveSelectedBand,
        currentUserId,
        isLeavingBand,
        refreshSelectedBandDetails,
        selectedBand?.id,
        setBands,
    ]);

    const handleSaveBandLocation = useCallback(async (payload) => {
        if (!selectedBand?.id) return null;
        const bandId = selectedBand.id;
        const requestId = previewRequestIdRef.current;
        setPreviewError(null);
        setSelectedBand((current) => (
            current?.id === bandId ? mergeBandLocationPayload(current, payload) : current
        ));
        setBands((current) => current.map((band) => (
            band?.id === bandId ? mergeBandLocationPayload(band, payload) : band
        )));
        try {
            const updatedBand = await bandService.updateBandLocation(bandId, payload);
            if (previewRequestIdRef.current === requestId) {
                await refreshSelectedBandDetails();
            }
            return updatedBand;
        } catch (err) {
            if (previewRequestIdRef.current === requestId) {
                setPreviewError(err);
                await refreshSelectedBandDetails().catch(() => {});
            }
            throw err;
        }
    }, [refreshSelectedBandDetails, selectedBand?.id, setBands]);

    const handleSaveBandField = useCallback(async (field, value) => {
        if (!selectedBand?.id || !field) return;
        const bandId = selectedBand.id;
        let normalizedValue = value;
        try {
            if (field === "foundedYear") {
                normalizedValue = normalizeFoundedYearDraft(value, formatCreatedYear(selectedBand.createdAt));
            } else if (field === "styleTags") {
                normalizedValue = normalizeStyleTagList(value, { splitCommas: true });
            }
        } catch (err) {
            setPreviewError(err);
            throw err;
        }
        const requestId = previewRequestIdRef.current;
        const saveRevision = bandFieldSaveRevisionRef.current + 1;
        bandFieldSaveRevisionRef.current = saveRevision;
        const fieldSaveVersion = (bandFieldSaveVersionByFieldRef.current[field] || 0) + 1;
        bandFieldSaveVersionByFieldRef.current[field] = fieldSaveVersion;
        setPreviewError(null);
        setSelectedBand((prev) => applyBandFieldValue(prev, field, normalizedValue));
        try {
            await enqueueBandFieldSave(bandFieldSaveQueueRef, {
                bandId,
                field,
                value: normalizedValue,
                saveBandField: async ({ field: fieldName, value: fieldValue, signal }) => {
                    const requestOptions = { signal };
                    if (fieldName === "name") {
                        return bandService.updateBandName(bandId, fieldValue, requestOptions);
                    }
                    if (fieldName === "styleTags") {
                        const nextTags = Array.isArray(fieldValue) ? fieldValue : [];
                        return bandService.updateBand(bandId, {
                            genres: nextTags,
                            styles: nextTags,
                        }, requestOptions);
                    }
                    if (fieldName === "foundedYear") {
                        return bandService.updateBand(bandId, { foundedYear: fieldValue }, requestOptions);
                    }
                    if (fieldName === "locationDisplay") {
                        return bandService.updateBandLocation(
                            bandId,
                            buildLocationDisplayUpdatePayload(selectedBand, fieldValue),
                            requestOptions,
                        );
                    }
                    return bandService.updateBand(bandId, { [fieldName]: fieldValue ?? "" }, requestOptions);
                },
                onWriteSuccess: (result, context) => {
                    if (previewRequestIdRef.current !== requestId) {
                        return;
                    }
                    const confirmedValue = hasBandFieldValue(result, field)
                        ? readBandFieldValue(result, field)
                        : context.value;
                    markConfirmedBandFieldValue(bandId, field, confirmedValue);
                },
                refreshBand: refreshSelectedBandDetails,
            });
            if (field === "name") {
                const nextPath = buildBandPath({ id: bandId, handle: selectedBand?.handle, name: normalizedValue });
                const nextRouteBandLookupKey = buildRouteBandLookupKey(getBandRouteLocator(nextPath));
                if (nextPath && nextRouteBandLookupKey && location.pathname !== nextPath) {
                    routeBandLookupKeyRef.current = nextRouteBandLookupKey;
                    pendingBandPathRef.current = nextPath;
                    navigate(nextPath, { replace: true });
                }
            }
        } catch (err) {
            if (previewRequestIdRef.current === requestId) {
                setPreviewError(err);
                if (bandFieldSaveVersionByFieldRef.current[field] === fieldSaveVersion) {
                    const confirmedValue = readConfirmedBandFieldValue(bandId, field, selectedBand);
                    setSelectedBand((current) => (
                        current?.id === bandId ? applyBandFieldValue(current, field, confirmedValue) : current
                    ));
                }
                if (bandFieldSaveRevisionRef.current === saveRevision) {
                    await refreshSelectedBandDetails();
                }
            }
            throw err;
        }
    }, [
        location.pathname,
        markConfirmedBandFieldValue,
        navigate,
        readConfirmedBandFieldValue,
        refreshSelectedBandDetails,
        selectedBand,
    ]);

    const handleSaveMemberInstrument = useCallback(async (memberUserId, instrument) => {
        if (!selectedBand?.id || !currentUserId || !memberUserId) {
            throw new Error("Band member instrument updates require an authenticated band member.");
        }
        const bandId = selectedBand.id;
        const requestId = previewRequestIdRef.current;
        setPreviewError(null);
        try {
            const updatedMember = await bandService.updateMemberMainInstrument(
                bandId,
                memberUserId,
                instrument,
            );
            if (previewRequestIdRef.current !== requestId) {
                return updatedMember;
            }
            setSelectedBand((prev) => mergeUpdatedBandMember(prev, memberUserId, updatedMember, instrument));
            setBands((current) => current.map((band) => (
                band?.id === bandId
                    ? mergeUpdatedBandMember(band, memberUserId, updatedMember, instrument)
                    : band
            )));
            await refreshSelectedBandDetails();
            return updatedMember;
        } catch (err) {
            if (previewRequestIdRef.current === requestId) {
                setPreviewError(err);
            }
            throw err;
        }
    }, [currentUserId, refreshSelectedBandDetails, selectedBand?.id, setBands]);

    const loadSelectedBandFollowersPage = useCallback((options = {}) => {
        const bandId = selectedBand?.id;
        if (!bandId) {
            return Promise.resolve({
                items: [],
                hasMore: false,
                limit: null,
                offset: 0,
                nextOffset: null,
                totalCount: 0,
            });
        }
        return bandService.getBandFollowersPage(bandId, options);
    }, [selectedBand?.id]);

    const detailsViewProps = selectedBand ? {
        previewError,
        bandPageProps: {
            band: selectedBand,
            coverImageUrl: selectedBandCoverUrl,
            avatarImageUrl: selectedBandAvatarUrl,
            onBack: handleClosePreview,
            backButtonPlacement: "tabbar",
            onShare: shareBand,
            onPublicView: canEditSelectedBand ? handleOpenSelectedBandPublicView : undefined,
            onVisibilityChange: canEditSelectedBand ? handleSelectedBandVisibilityChange : undefined,
            isVisibilityUpdating,
            onFollow: canShowFollowSelectedBand ? handleToggleSelectedBandFollow : undefined,
            onLeaveBand: canLeaveSelectedBand ? handleRequestLeaveBand : undefined,
            isFollowing: selectedBandIsFollowing,
            isFollowLoading,
            isLeaveBandLoading: isLeavingBand,
            followers: bandFollowers,
            followersLoaded: bandFollowersLoaded,
            loadFollowersPage: loadSelectedBandFollowersPage,
            onFollowerClick: handleOpenUserProfile,
            onInviteMember: canEditSelectedBand ? handleOpenInviteMemberModal : undefined,
            onCreateEvent: canEditSelectedBand ? handleOpenCreateEvent : undefined,
            onEditEvent: canEditSelectedBand ? handleOpenEditEvent : undefined,
            onDeleteEvent: canEditSelectedBand ? handleRequestDeleteBandEvent : undefined,
            canEdit: canEditSelectedBand,
            currentUserId,
            posts: bandPosts.posts,
            tracks: bandMedia.tracks,
            playsCount: bandMedia.playsCount,
            playingTrackId: bandMedia.playingTrackId,
            trackProgressById: bandMedia.trackProgressById,
            deletingTrackId: bandMedia.deletingTrackId,
            isTrackReordering: bandMedia.isTrackReordering,
            galleryPhotos: bandMedia.galleryPhotos,
            deletingGalleryPhotoId: bandMedia.deletingGalleryPhotoId,
            isGalleryPhotoReordering: bandMedia.isGalleryPhotoReordering,
            onStartPost: canEditSelectedBand ? bandPosts.openCreateComposer : undefined,
            onUploadTrack: canEditSelectedBand ? bandMedia.handleOpenBandTrackUpload : undefined,
            onDeleteTrack: canEditSelectedBand ? bandMedia.handleDeleteBandTrack : undefined,
            onMoveTrack: canEditSelectedBand ? bandMedia.handleMoveBandTrack : undefined,
            onPlayTrack: bandMedia.handlePlayTrack,
            onSeekTrack: bandMedia.handleSeekTrack,
            onUploadPhoto: canEditSelectedBand ? bandMedia.handleOpenBandGalleryPhotoUpload : undefined,
            onDeletePhoto: canEditSelectedBand ? bandMedia.handleDeleteBandGalleryPhoto : undefined,
            onReorderPhotos: canEditSelectedBand ? bandMedia.handleReorderBandGalleryPhotos : undefined,
            postComposerProps: bandPostComposerProps,
            onSaveField: canEditSelectedBand ? handleSaveBandField : undefined,
            onSaveLocation: canEditSelectedBand ? handleSaveBandLocation : undefined,
            onMemberClick: handleOpenUserProfile,
            onSaveMemberInstrument: currentUserId ? handleSaveMemberInstrument : undefined,
            onRemoveMember: canEditSelectedBand ? handleRequestRemoveBandMember : undefined,
            removingMemberUserId: isRemovingMember ? memberPendingRemoval?.userId || null : null,
            onEditCover: canEditSelectedBand ? handleOpenBandCoverUpload : undefined,
            onEditAvatar: canEditSelectedBand ? handleOpenBandAvatarUpload : undefined,
        },
        coverUploadProps: {
            isOpen: isBandCoverUploadOpen,
            onClose: handleCloseBandCoverUpload,
            onUploadSuccess: handleBandCoverUploaded,
            currentUrl: selectedBandCoverRawUrl,
            mediaConfig: bandCoverUploadConfig,
        },
        avatarUploadProps: {
            isOpen: isBandAvatarUploadOpen,
            onClose: handleCloseBandAvatarUpload,
            onUploadSuccess: handleBandAvatarUploaded,
            currentUrl: selectedBandAvatarRawUrl,
            mediaConfig: bandAvatarUploadConfig,
        },
        galleryUploadProps: {
            inputRef: bandMedia.bandGalleryPhotoInputRef,
            onChange: bandMedia.handleBandGalleryPhotoFileChange,
        },
        trackUploadProps: {
            inputRef: bandMedia.bandTrackInputRef,
            onChange: bandMedia.handleBandTrackFileChange,
        },
        eventModalProps: {
            isOpen: isEventModalOpen,
            mode: eventModalMode,
            event: eventBeingEdited,
            onClose: handleCloseEventModal,
            onCreated: handleBandEventCreated,
            onUpdated: handleBandEventUpdated,
            onDeleted: handleBandEventDeleted,
            organizer: bandEventOrganizer,
        },
        eventDeleteDialogProps: {
            isOpen: Boolean(eventToDelete),
            targetName: eventToDelete?.title || null,
            isSubmitting: isDeletingBandEvent,
            errorMessage: bandEventDeleteError,
            onConfirm: handleConfirmDeleteBandEvent,
            onCancel: handleCancelDeleteBandEvent,
        },
        inviteMemberModalProps: {
            isOpen: isInviteMemberModalOpen,
            bandName: selectedBand.name || "",
            searchTerm: inviteMemberSearchTerm,
            onSearchChange: setInviteMemberSearchTerm,
            candidates: inviteMemberCandidates,
            isLoading: isInviteMemberLoading,
            invitingUserId: invitingMemberUserId,
            error: inviteMemberError,
            onInvite: handleInviteBandMember,
            onClose: handleCloseInviteMemberModal,
        },
        memberRemoveDialogProps: {
            isOpen: Boolean(memberPendingRemoval),
            targetName: memberPendingRemoval?.displayName || memberPendingRemoval?.username || null,
            // Pending invitees are revoked (not removed); the dialog swaps its copy.
            isPending: Boolean(memberPendingRemoval?.isPending)
                || isPendingBandMembershipStatus(memberPendingRemoval?.membershipStatus),
            isSubmitting: isRemovingMember,
            error: memberRemoveError,
            onConfirm: handleConfirmRemoveBandMember,
            onCancel: handleCancelRemoveBandMember,
        },
        leaveBandDialogProps: {
            isOpen: isLeaveBandDialogOpen,
            targetName: selectedBand.name || null,
            isSubmitting: isLeavingBand,
            error: leaveBandError,
            onConfirm: handleConfirmLeaveBand,
            onCancel: handleCancelLeaveBand,
        },
        shareToastProps: {
            toast: bandShareToast,
            onClose: clearBandShareToast,
        },
        mediaToastProps: {
            toast: bandMedia.toast,
            onClose: bandMedia.clearToast,
        },
    } : null;

    return {
        selectedBand,
        detailsViewProps,
        handleOpenBand,
        isRoutePreviewLoading: Boolean(isPreviewLoading && routeBandLookupKey),
        routePreviewError: routeBandLookupKey ? previewError : null,
    };
}
